import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@boma/app-config';
import { IPaginatedResponse } from '@boma/client-shared';
import {
  IOrder,
  IOrderBatchResponse,
  IOrderCreate,
  IOrderItem,
  IOrderMessage,
  OrderDirection,
  OrderEventStatus,
  ICreditNoteResponse,
} from '@boma/supplier-portal/shared/entities';
import { HelperFunctionsService } from '@boma/supplier-portal/shared/services';
import { IOrderHandshakeOffer } from '@boma/website/shared/entities';
import { map, tap, catchError } from 'rxjs/operators';
import { TransferListItem } from '../../../../../transfers/src/lib/models/transfer-list-item';

@Injectable({
  providedIn: 'root',
})
export class OrdersService {
  private base: string;
  private version: string;

  constructor(
    @Inject(APP_CONFIG) private configuration: any,
    private httpClient: HttpClient,
    private helperService: HelperFunctionsService
  ) {
    this.base = configuration.apiRoute;
    this.version = configuration.apiVersion;
  }

  getOrders(
    entityId: string = null,
    locationIds: string[] = [],
    page: string = '1',
    perPage: string = '25',
    query: string = '',
    direction: OrderDirection = OrderDirection.Inbound,
    batchId: string = null,
    customerEntityIds: string[] = [],
    dateRange: { fromDate: string; toDate: string },
    statuses: string[] = [
      'Placed',
      'Processing',
      'Accepted',
      'Loading',
      'Dispatched',
    ],
    orderTypes: string[] = ['Order', 'InternalOrder']
  ) {
    // TODO Handle pagination
    const params = {
      query: query,
      batchId: batchId,
      direction: OrderDirection[direction],
      status: status,
      startDate: dateRange?.fromDate,
      endDate: dateRange?.toDate,
      locationIds: locationIds,
      customerEntityIds: customerEntityIds,
      statuses: statuses,
      orderTypes: orderTypes,
    };

    if (!entityId) return;
    console.log(entityId);
    console.log(params);
    if (!page) page = '1';
    return this.httpClient.post<IPaginatedResponse<IOrder>>(
      `${this.base}api/v${this.version}/${entityId}/Orders?page=${page}&perPage=${perPage}`,
      this.helperService.sanitizeParams(params)
    );
  }

  getOrder(entityId: string, id: string, withNotes: boolean = true) {
    return this.httpClient.get<IOrder>(
      `${this.base}api/v${this.version}/${entityId}/Orders/${id}?withNotes=${withNotes}`
    );
  }

  //TODO Move to sales service even though it's the same atm.
  getSales(params: {
    entityId: string;
    page?: number;
    perPage?: number;
    query?: string;
    batchId?: string;
    status?: string; // TODO can remove this... and just use statuses
    startDate?: Date;
    endDate?: Date;
    locationIds?: string[];
    customerEntityIds?: string[];
    statuses?: string[];
    orderType?: string;
    orderTypes?: string[];
  }) {
    console.log('🌐 [ORDERS SERVICE] getSales called with params:', params);

    params['direction'] = OrderDirection.Outbound;
    if (!params['page']) params['page'] = 1;
    if (!params['perPage']) params['perPage'] = 25;
    if (!params['statuses'])
      params['statuses'] = ['Fulfilled', 'Cancelled', 'Returned'];
    if (!params['orderTypes'] && !params['orderType'])
      params['orderTypes'] = ['Sale', 'Order'];

    const endpoint = `${this.base}api/v${this.version}/${params.entityId}/Orders?page=${params.page}&perPage=${params.perPage}`;
    const sanitizedParams = this.helperService.sanitizeParams(params);

    console.log('🌐 [ORDERS SERVICE] POST endpoint:', endpoint);
    console.log('🌐 [ORDERS SERVICE] Request body:', sanitizedParams);

    return this.httpClient.post<IPaginatedResponse<IOrder>>(
      endpoint,
      sanitizedParams
    ).pipe(
      tap(response => {
        console.log('✅ [ORDERS SERVICE] Response received:', {
          totalItems: response.totalItems,
          itemsCount: response.items?.length,
          page: response.page
        });
        console.log('✅ [ORDERS SERVICE] Orders:', response.items);
      }),
      catchError(error => {
        console.error('❌ [ORDERS SERVICE] Error:', error);
        throw error;
      })
    );
  }

  getTransfers(params: {
    entityId: string;
    page?: number;
    perPage?: number;
    query?: string;
    batchId?: string;
    status?: string;
    startDate?: Date;
    endDate?: Date;
    locationIds?: string[];
    customerEntityIds?: string[];
    statuses?: string[];
    orderTypes?: string[];
    direction?: OrderDirection;
  }) {
    params['orderType'] = 'Transfer';
    if (!params['page']) params['page'] = 1;
    if (!params['perPage']) params['perPage'] = 25;

    return this.httpClient.post<IPaginatedResponse<IOrder>>(
      `${this.base}api/v${this.version}/${params.entityId}/Orders?page=${params.page}&perPage=${params.perPage}`,
      this.helperService.sanitizeParams(params)
    );
  }

  getTransfersV2(params: {
    entityId: string;
    page?: number;
    perPage?: number;
    body: {
      query?: string;
      batchId?: number;
      startDate?: Date;
      endDate?: Date;
      locationIds?: string[];
      statuses?: OrderEventStatus[];
    };
  }) {
    if (!params['page']) params['page'] = 1;
    if (!params['perPage']) params['perPage'] = 25;

    return this.httpClient
      .post<IPaginatedResponse<TransferListItem>>(
        `${this.base}api/v${this.version}/${params.entityId}/Orders/Transfers/List?page=${params.page}&pageSize=${params.perPage}`,
        params.body
      )
      .pipe(
        map(
          (x) =>
            ({
              items: x.items.map((item) => new TransferListItem(item)),
              page: x.page,
              totalItems: x.totalItems,
              nextPage: x.nextPage,
              previousPage: x.previousPage,
              totalPages: x.totalPages,
            }) as IPaginatedResponse<TransferListItem>
        )
      );
  }

  updateOrderStatusToAccepted(entityId: string, id: string, comment: string) {
    return this.httpClient.post<IOrder>(
      `${this.base}api/v${this.version}/${entityId}/Orders/${id}/ChangeStatus/Accepted`,
      { comment }
    );
  }

  updateOrderStatusToProcessing(entityId: string, id: string, comment: string) {
    return this.httpClient.post<IOrder>(
      `${this.base}api/v${this.version}/${entityId}/Orders/${id}/ChangeStatus/Processing`,
      { comment }
    );
  }

  updateOrderStatusToLoading(entityId: string, id: string, comment: string) {
    return this.httpClient.post<IOrder>(
      `${this.base}api/v${this.version}/${entityId}/Orders/${id}/ChangeStatus/Loading`,
      { comment }
    );
  }

  updateOrderStatusToCancelled(
    entityId: string,
    id: string,
    comment: string,
    direction: OrderDirection
  ) {
    return this.httpClient.post<IOrder>(
      `${this.base}api/v${this.version}/${entityId}/Orders/${id}/ChangeStatus/Cancelled?direction=${direction}`,
      { comment }
    );
  }

  updateOrderStatusToDispatched(
    entityId: string,
    id: string,
    comment: string,
    vehicleId: string,
    driverId: string
  ) {
    return this.httpClient.post<IOrder>(
      `${this.base}api/v${this.version}/${entityId}/Orders/${id}/ChangeStatus/Dispatched`,
      { comment, vehicleId, driverId }
    );
  }

  updateOrderStatusToFulfilled(
    entityId: string,
    id: string,
    comment: string,
    userId: string,
    userPaidCashAmount: boolean
  ) {
    const params = {
      userId: userId,
      digest: null,
      userPaidCashAmount: userPaidCashAmount,
      latitude: null,
      longitude: null,
      comment: comment,
    };

    return this.httpClient.post<IOrder>(
      `${this.base}api/v${this.version}/${entityId}/Orders/${id}/ChangeStatus/FulFilled`,
      params
    );
  }

  loadOrderMessages(entityId: string, id: string, direction: OrderDirection) {
    return this.httpClient.get<IOrderMessage[]>(
      `${this.base}api/v${this.version}/${entityId}/Orders/${id}/Messages?direction=${direction}`
    );
  }

  createOrderMessage(
    entityId: string,
    id: string,
    direction: OrderDirection,
    content: string
  ) {
    return this.httpClient.post<IOrderMessage>(
      `${this.base}api/v${this.version}/${entityId}/Orders/${id}/Messages?direction=${direction}`,
      { content }
    );
  }

  markOrderChargeStatusAsPaid(entityId: string, id: string, chargeId: string) {
    return this.httpClient.post<IOrder>(
      `${this.base}api/v${this.version}/${entityId}/Orders/${id}/Charges/${chargeId}/MarkAsPaid`,
      {}
    );
  }

  refundOrderCharge(
    entityId: string,
    id: string,
    chargeId: string,
  ) {
    return this.httpClient.put<IOrder>(
      `${this.base}api/v${this.version}/${entityId}/Orders/${id}/Charges/${chargeId}/Refund`,
      {}
    );
  }

  changeOrderChargePaymentMethod(
    entityId: string,
    id: string,
    chargeId: string,
    paymentMethod: string
  ) {
    return this.httpClient.post<IOrder>(
      `${this.base}api/v${this.version}/${entityId}/Orders/${id}/Charges/${chargeId}/ChangePaymentMethod`,
      { type: paymentMethod }
    );
  }

  updateOrderSupplyingLocation(params: {
    entityId: string;
    orderId: string;
    locationId: string;
    addMissingProducts?: boolean;
  }) {
    const { entityId, orderId, locationId, addMissingProducts } = params;
    return this.httpClient.put<IOrder>(
      `${this.base}api/v${
        this.version
      }/${entityId}/Orders/${orderId}/SupplyingLocation/${locationId}?addMissingProducts=${
        addMissingProducts || false
      }`,
      null
    );
  }

  // To create a POS sale we simply remove the receiving location currently.
  // Also pass the value pos sale.

  // {
  //   "receivingLocationId": "3fa85f64-5717-4562-b3fc-2c963f66afa6",
  //   "supplyingLocationId": "3fa85f64-5717-4562-b3fc-2c963f66afa6",
  //   "items": [
  //     {
  //       "productId": "3fa85f64-5717-4562-b3fc-2c963f66afa6",
  //       "units": 0,
  //       "price": 0
  //     }
  //   ],
  //   "isDirectSale": true,
  //   "isPos": true,
  //   "comment": "string"
  // }

  createOrder(
    entityId: string,
    receivingLocationId: string,
    saleLocationId: string,
    items: IOrderItem[],
    isDirectSale: boolean = false,
    isPos: boolean = false,
    orderPaymentType: string,
    comment: string,
    idempotencyKey?: string,
    currency?: string,
    exchangeRate?: number
  ) {
    const params: any = {
      saleLocationId,
      comment: comment,
      items: items,
      isDirectSale: isDirectSale,
      isPos: isPos,
      orderPaymentType: orderPaymentType,
    };

    if (receivingLocationId) {
      params['receivingLocationId'] = receivingLocationId;
    }

    if (currency && currency !== 'ZMW') {
      params['currency'] = currency;
      params['exchangeRate'] = exchangeRate;
    }

    let headers = new HttpHeaders();
    if (idempotencyKey)
      headers = headers.append('boma-idempotency-key', idempotencyKey);
    return this.httpClient.post<IOrderCreate>(
      `${this.base}api/v${this.version}/${entityId}/Orders/Create`,
      params,
      {
        headers,
      }
    );
  }

  createOrderForCustomer(
    entityId: string,
    operatingLocationId: string,
    receivingLocationId: string,
    saleLocationId: string,
    items: IOrderItem[],
    isDirectSale: boolean = false,
    isPos: boolean = false,
    orderPaymentType: string,
    comment: string,
    externalOrderId: string,
    externalInvoiceId: string,
    idempotencyKey?: string,
    isDebitNote: boolean = false,
    currency?: string,
    exchangeRate?: number
  ) {
    console.log('creating order service');
    const params: any = {
      saleLocationId,
      comment: comment,
      items: items,
      isDirectSale: isDirectSale,
      isPos: isPos,
      orderPaymentType: orderPaymentType,
      externalInvoiceId: externalInvoiceId,
      isDebitNote: isDebitNote,
    };

    // Only include externalOrderId when it's a debit note
    // The backend uses externalOrderId presence to determine debit note type
    if (isDebitNote && externalOrderId) {
      params.externalOrderId = externalOrderId;
    }

    if (currency && currency !== 'ZMW') {
      params.currency = currency;
      params.exchangeRate = exchangeRate;
    }

    console.log(params);
    if (receivingLocationId) {
      params['receivingLocationId'] = receivingLocationId;
    }

    let headers = new HttpHeaders();
    if (idempotencyKey) {
      headers = headers.append('boma-idempotency-key', idempotencyKey);
    }

    return this.httpClient.post<IOrderBatchResponse>(
      `${this.base}api/v${this.version}/${entityId}/Orders/${operatingLocationId}/Customer`,
      params,
      {
        headers,
      }
    );
  }

  cancelFulfilledOrder(
    entityId: string,
    orderId: string,
    cancelReason: string = 'Sale',
    restockLocationId: string,
    comment: string,
    latitude = 0,
    longitude = 0
  ) {
    //Add longitude latitude... if required not really seeing a point to do this
    const params = this.helperService.sanitizeParams({
      cancelReason: cancelReason,
      restockLocationId: restockLocationId,
      comment: comment,
      latitude: latitude,
      longitude: longitude,
    });

    return this.httpClient.post<IOrder>(
      `${this.base}api/v${this.version}/${entityId}/Orders/${orderId}/ChangeStatus/CancelledFromFulfilled`,
      params
    );
  }

  getOrderHandshakeOffer(operatingEntityId: string, orderId: string) {
    return this.httpClient.get<IOrderHandshakeOffer>(
      `${this.base}api/v${this.version}/${operatingEntityId}/Orders/${orderId}/Handshake/Offer`
    );
  }

  getLatestCreditNoteSubmission(entityId: string, id: string, includeFailed: boolean = false) {
    return this.httpClient.get<ICreditNoteResponse>(
      `${this.base}api/v${this.version}/${entityId}/Orders/${id}/CreditNotes/Latest?includeFailed=${includeFailed}`
    );
  }

  refundOrder(entityId: string, id: string) {
    return this.httpClient.post<ICreditNoteResponse>(
      `${this.base}api/v${this.version}/${entityId}/Orders/${id}/Refund`,
      {}
    );
  }
}
